// src/hooks/usePayments.ts
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '@/lib/queryClient';
import { getAccessToken } from '@/lib/authToken';
import type {
  PaymentPackage,
  CreateCheckoutSessionRequest,
  CreateCheckoutSessionResponse,
  ChargeTokenRequest,
  ChargeTokenResponse,
  CurrentPackageResponse,
  PaymentMethodItem,
  AddPaymentMethodRequest,
  AddPaymentMethodResponse,
  PaymentHistoryItem,
  TransactionStatus,
  PaddleChargeSavedResponse,
} from '@/types/payments';

function normalizePaymentMethodItem(item: any): PaymentMethodItem {
  return {
    id: item?.id,
    provider: item?.provider,
    scheme: item?.scheme || item?.card_scheme || item?.brand || 'Card',
    last4: item?.last4 || item?.card_last4 || item?.last_four || undefined,
    exp_month: item?.exp_month != null ? Number(item.exp_month) : undefined,
    exp_year: item?.exp_year != null ? Number(item.exp_year) : undefined,
    status: item?.status,
    created_at: item?.created_at,
    is_default: Boolean(item?.is_default),
  };
}

export const PAYMENTS_KEYS = {
  packages: ['/api/payments/packages'] as const,
  current: ['/api/payments/current-package'] as const,
  methods: ['/api/payments/methods'] as const,
  history: ['/api/payments/history'] as const,
};

export function usePaymentPackages() {
  return useQuery<PaymentPackage[]>({
    queryKey: PAYMENTS_KEYS.packages,
    queryFn: async () => {
      const res = await apiRequest('GET', '/api/payments/packages');
      return res.json();
    },
    // Inherit global always-fresh policy
  });
}

export function useCurrentPackage() {
  return useQuery<CurrentPackageResponse>({
    queryKey: PAYMENTS_KEYS.current,
    queryFn: async () => {
      const res = await apiRequest('GET', '/api/payments/current-package');
      return res.json();
    },
    enabled: !!getAccessToken(),
    // Inherit global always-fresh policy
  });
}

export function usePaymentMethods() {
  return useQuery<PaymentMethodItem[]>({
    queryKey: PAYMENTS_KEYS.methods,
    queryFn: async () => {
      const res = await apiRequest('GET', '/api/payments/methods');
      const json = await res.json();
      const rawList = Array.isArray(json)
        ? json
        : Array.isArray(json?.data)
        ? json.data
        : Array.isArray(json?.items)
        ? json.items
        : [];

      return rawList
        .map(normalizePaymentMethodItem)
        .filter((item: PaymentMethodItem) => item.id != null);
    },
    enabled: !!getAccessToken(),
    // Inherit global always-fresh policy
  });
}

export function usePaymentHistory() {
  return useQuery<PaymentHistoryItem[]>({
    queryKey: PAYMENTS_KEYS.history,
    queryFn: async () => {
      const res = await apiRequest('GET', '/api/payments/history');
      return res.json();
    },
    enabled: !!getAccessToken(),
  });
}

// Single-shot fetcher for transaction status (used by custom polling)
export async function fetchTransactionStatus(paymentId: string | number): Promise<TransactionStatus> {
  const res = await apiRequest('GET', `/api/payments/transactions/${paymentId}`);
  return res.json();
}

// Wait for a transaction to complete or timeout in the backend
export interface WaitForTransactionOptions {
  timeout?: number; // seconds
  includeInvoice?: boolean;
  includePresigned?: boolean;
  invoiceUrlExpires?: number; // seconds
}

export interface WaitTransactionResponse extends TransactionStatus {
  timed_out?: boolean;
  invoice_id?: string | number;
  invoice_public_url?: string | null;
  invoice_presigned_url?: string | null;
}

export async function waitForTransaction(
  paymentId: string | number,
  opts?: WaitForTransactionOptions,
): Promise<WaitTransactionResponse> {
  const timeout = opts?.timeout ?? 60;
  const includeInvoice = opts?.includeInvoice ?? true;
  const includePresigned = opts?.includePresigned ?? true;
  const invoiceUrlExpires = opts?.invoiceUrlExpires ?? 180;
  const q = new URLSearchParams({
    timeout: String(timeout),
    include_invoice: includeInvoice ? 'true' : 'false',
    include_presigned: includePresigned ? 'true' : 'false',
    invoice_url_expires: String(invoiceUrlExpires),
  });
  const res = await apiRequest('GET', `/api/payments/transactions/${paymentId}/wait?${q.toString()}`);
  return res.json();
}

export function useCreateCheckoutSession() {
  return useMutation<
    CreateCheckoutSessionResponse,
    Error,
    CreateCheckoutSessionRequest
  >({
    mutationFn: async (payload) => {
      // Some backends expose session creation under slightly different routes.
      // Prefer env override if provided, else try the common defaults.
      const envPath = (import.meta as any).env?.VITE_CHECKOUT_SESSION_PATH as string | undefined;
      const primary = envPath || '/api/payments/checkout/session';
      let res: Response;
      try {
        res = await apiRequest('POST', primary, payload);
      } catch (e: any) {
        // If 404, try a couple of known fallbacks before surfacing error
        const msg = String(e?.message || '');
        if (/^404:/.test(msg)) {
          const fallbacks = [
            // generic payment session
            '/api/payments/session',
            '/api/checkout/session',
            // setup-only common variants
            '/api/payments/setup/session',
            '/api/payments/methods/session',
            '/api/payments/methods/setup-session',
          ];
          let lastErr = e;
          for (const p of fallbacks) {
            try {
              res = await apiRequest('POST', p, payload);
              // If no throw, we have a response
              return res.json();
            } catch (ee) {
              lastErr = ee;
            }
          }
          throw lastErr;
        }
        throw e;
      }
      return res.json();
    },
  });
}

export function useChargeToken() {
  const qc = useQueryClient();
  return useMutation<ChargeTokenResponse, Error, ChargeTokenRequest>({
    mutationFn: async (payload) => {
      const res = await apiRequest('POST', '/api/payments/checkout/charge-token', payload);
      return res.json();
    },
    onSuccess: (data) => {
      if (data?.success) {
        qc.invalidateQueries({ queryKey: PAYMENTS_KEYS.current });
        qc.invalidateQueries({ queryKey: PAYMENTS_KEYS.history });
        // Also refresh credits and user profile so header updates immediately
        qc.invalidateQueries({ queryKey: ["/api/credits/balance"] });
        qc.invalidateQueries({ queryKey: ["/api/credits/summary"] });
        qc.invalidateQueries({ queryKey: ["/api/user/profile"] });
      }
    },
  });
}

export function useAddPaymentMethod() {
  const qc = useQueryClient();
  return useMutation<AddPaymentMethodResponse, Error, AddPaymentMethodRequest>({
    mutationFn: async (payload) => {
      const res = await apiRequest('POST', '/api/payments/methods', payload);
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: PAYMENTS_KEYS.methods });
    },
  });
}

export function useSetDefaultPaymentMethod() {
  const qc = useQueryClient();
  return useMutation<{ ok: boolean; default_method_id?: string | number }, Error, { id: string | number }>({
    mutationFn: async ({ id }) => {
      const res = await apiRequest('PATCH', `/api/payments/methods/${id}/default`);
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: PAYMENTS_KEYS.methods });
    },
  });
}

export function useDeletePaymentMethod() {
  const qc = useQueryClient();
  return useMutation<{ deleted: boolean }, Error, { id: string | number }>({
    mutationFn: async ({ id }) => {
      const res = await apiRequest('DELETE', `/api/payments/methods/${id}`);
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: PAYMENTS_KEYS.methods });
    },
  });
}

// Paddle one-click charge with saved method
export function usePaddleChargeSaved() {
  const qc = useQueryClient();
  return useMutation<PaddleChargeSavedResponse, Error, { package_code: string | number; payment_method_id?: string | number }>({
    mutationFn: async ({ package_code, payment_method_id }) => {
      const payload: Record<string, any> = { package_code };
      if (payment_method_id != null) payload.payment_method_id = payment_method_id;
      const res = await apiRequest('POST', '/api/payments/paddle/charge-saved', payload);
      return res.json();
    },
    onSuccess: (data) => {
      if (data?.success) {
        // Invalidate package + credits + history regardless of immediate status
        qc.invalidateQueries({ queryKey: PAYMENTS_KEYS.current });
        qc.invalidateQueries({ queryKey: PAYMENTS_KEYS.history });
        qc.invalidateQueries({ queryKey: ["/api/credits/balance"] });
        qc.invalidateQueries({ queryKey: ["/api/credits/summary"] });
        qc.invalidateQueries({ queryKey: ["/api/user/profile"] });
        qc.invalidateQueries({ queryKey: PAYMENTS_KEYS.methods });
      }
    },
  });
}
